import { curSym } from "@/lib/company";
import { TaxOfficePicker } from "@/components/TaxOfficePicker";
import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Plus, Pencil, Trash2, Loader2, Phone, User, Mail, MapPin, X, FileSpreadsheet, Hash, Layers, Search, TrendingUp, Tag, Download, Eye } from "lucide-react";
import { TR_CITIES } from "@/lib/tr-cities";
import { TR_REGIONS } from "@/lib/regions";
import SupplierSummary from "@/components/SupplierSummary";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { EntityAvatar, AvatarUpload } from "@/components/EntityAvatar";
import TaxPortalLookup from "@/components/TaxPortalLookup";
import { PriceInput } from "@/components/PriceInput";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import { useTablePrefs } from "@/lib/useTablePrefs";
import { ColumnToggle } from "@/components/ColumnToggle";

const PROVINCES = Object.keys(TR_CITIES).sort((a, b) => a.localeCompare(b, "tr"));
const EMPTY = {
  name: "", type: "corporate", avatar_url: "", contact: "", phone: "", email: "", tax_office: "", tax_no: "",
  province: "", district: "", address: "", note: "", supply_categories: [], product_note: "", e_invoice_portal: "", e_document_type: "", tax_verified_at: "",
  payment_term_days: "", credit_limit: "",
};

const SUPP_COLS = [
  { key: "name", label: "Tedarikçi", locked: true },
  { key: "contact", label: "Yetkili & İletişim" },
  { key: "tax", label: "V/TC No" },
  { key: "location", label: "İl / İlçe" },
  { key: "terms", label: "Ödeme Vadesi" },
  { key: "cats", label: "Tedarik Kategorileri" },
];

export default function SupplierManager({ onChanged }) {
  const { can, user, feature } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = can("supplier.write");
  const { visible: colVis, density: rowDensity, toggle: toggleCol, setDensity: setColDensity, reset: resetCols } = useTablePrefs("suppliers", SUPP_COLS);
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selCat, setSelCat] = useState("");
  const [selSub, setSelSub] = useState("");
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fProvince, setFProvince] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [summaryId, setSummaryId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailDocs, setDetailDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { setSelected(new Set()); }, [list]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (fCategory) p.set("category", fCategory);
    if (fProvince) p.set("province", fProvince);
    else if (fRegion) p.set("region", fRegion);
    const qs = p.toString();
    api.get(`/suppliers${qs ? `?${qs}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  }, [fCategory, fProvince, fRegion]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const subsOf = (catName) => cats.find((c) => c.name === catName)?.subcategories || [];

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selSuppliers = list.filter((s) => selected.has(s.id));
  const bulkTag = async () => {
    const tag = window.prompt("Seçili tedarikçilere eklenecek etiket:");
    if (!tag || !tag.trim()) return;
    try { const { data } = await api.post("/suppliers/bulk-tag", { ids: [...selected], tag: tag.trim() }); toast.success(`${data.modified} tedarikçiye "${data.tag}" etiketi eklendi`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Etiketleme başarısız"); }
  };
  const exportSelectedCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Ad", "Kod", "Yetkili", "Telefon", "E-posta", "VKN/TCKN", "İl", "İlçe", "Etiketler"];
    const rows = selSuppliers.map((s) => [s.name, s.code, s.contact, s.phone, s.email, s.tax_no, s.province, s.district, (s.tags || []).join("; ")].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...rows].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "secili-tedarikciler.csv"; a.click(); window.URL.revokeObjectURL(url);
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setSelCat(""); setSelSub(""); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", type: s.type || "corporate", avatar_url: s.avatar_url || "", contact: s.contact || "", phone: s.phone || "",
      email: s.email || "", tax_office: s.tax_office || "", tax_no: s.tax_no || "",
      province: s.province || "", district: s.district || "", address: s.address || "", note: s.note || "",
      supply_categories: s.supply_categories || [], product_note: s.product_note || "",
      payment_term_days: s.payment_term_days ?? "", credit_limit: s.credit_limit ?? "",
      e_invoice_portal: s.e_invoice_portal || "", e_document_type: s.e_document_type || "", tax_verified_at: s.tax_verified_at || "",
    });
    setSelCat(""); setSelSub(""); setOpen(true);
  };

  const addCat = () => {
    if (!selCat) return;
    const exists = form.supply_categories.find((c) => c.category === selCat && (c.subcategory || "") === (selSub || ""));
    if (exists) { setSelSub(""); return; }
    setForm((f) => ({ ...f, supply_categories: [...f.supply_categories, { category: selCat, subcategory: selSub || null }] }));
    setSelSub("");
  };
  const rmCat = (i) => setForm((f) => ({ ...f, supply_categories: f.supply_categories.filter((_, idx) => idx !== i) }));

  const requestSave = () => {
    if (!form.name.trim()) return toast.error("Firma/Ad zorunlu");
    if (form.phone && !isValidPhone(form.phone)) return toast.error("Telefon numarası geçersiz — ülke koduna göre hane sayısını kontrol edin");
    if (!isAdmin) {
      if (!(form.contact || "").trim()) return toast.error("Yetkili kişi zorunlu");
      if (!form.phone) return toast.error("Telefon zorunlu");
      if (!(form.email || "").trim()) return toast.error("E-posta zorunlu");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("E-posta geçersiz");
      if (!(form.tax_no || "").trim()) return toast.error(form.type === "corporate" ? "VKN zorunlu" : "TCKN zorunlu");
      if (feature("field.tax_office") && form.type === "corporate" && !(form.tax_office || "").trim()) return toast.error("Vergi dairesi zorunlu");
      if (!form.province) return toast.error("İl zorunlu");
      if (!form.district) return toast.error("İlçe zorunlu");
      if (!(form.address || "").trim()) return toast.error("Açık adres zorunlu");
      if (!(form.tax_verified_at || "").trim()) return toast.error("VKN/TCKN doğrulanmadan kayıt yapılamaz — 'Doğrula & Getir' ile doğrulayın");
    }
    setConfirmOpen(true);
  };
  const doSave = async () => {
    setSaving(true);
    const body = {
      name: form.name.trim(), type: form.type, avatar_url: form.avatar_url || null, contact: form.contact || null, phone: form.phone || null,
      email: form.email || null, tax_office: form.tax_office || null, tax_no: form.tax_no || null,
      e_invoice_portal: form.e_invoice_portal || null, e_document_type: form.e_document_type || null, tax_verified_at: form.tax_verified_at || null,
      province: form.province || null, district: form.district || null, address: form.address || null,
      note: form.note || null, supply_categories: form.supply_categories, product_note: form.product_note || null,
      payment_term_days: form.payment_term_days === "" || form.payment_term_days == null ? null : parseInt(form.payment_term_days, 10),
      credit_limit: form.credit_limit === "" || form.credit_limit == null ? null : parseFloat(form.credit_limit),
    };
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, body);
      else await api.post("/suppliers", body);
      toast.success(editing ? "Tedarikçi güncellendi" : "Tedarikçi eklendi");
      setConfirmOpen(false); setOpen(false); load(); onChanged?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openDetail = (s) => { setDetail(s); setDetailDocs(s.documents || []); };
  const addSupplierDoc = async (body) => { const { data } = await api.post(`/suppliers/${detail.id}/documents`, body); setDetailDocs((d) => [...d, data]); toast.success("Evrak eklendi"); };
  const delSupplierDoc = async (id) => { await api.delete(`/suppliers/${detail.id}/documents/${id}`); setDetailDocs((d) => d.filter((x) => x.id !== id)); toast.success("Evrak silindi"); };

  const del = async (s) => {
    if (!window.confirm(`"${s.name}" tedarikçisi silinsin mi?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Silindi"); load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/suppliers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "tedarikciler.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Dışa aktarım başarısız"); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/suppliers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`İçe aktarıldı: ${data.created} yeni, ${data.updated} güncellendi${data.skipped ? `, ${data.skipped} atlandı` : ""}`);
      load(); onChanged?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İçe aktarım başarısız"); }
  };

  const catLabel = (c) => c.category + (c.subcategory ? ` › ${c.subcategory}` : "");

  const ql = q.trim().toLocaleLowerCase("tr");
  const filtered = ql
    ? list.filter((s) => {
        const name = (s.name || "").toLocaleLowerCase("tr");
        return name.split(/\s+/).some((w) => w.startsWith(ql)) || name.startsWith(ql)
          || (s.phone || "").includes(ql) || (s.tax_no || "").includes(ql)
          || (s.code || "").toLocaleLowerCase("tr").includes(ql);
      })
    : list;
  const suppPaged = usePaged(filtered, 12);

  const req = {
    name: !!form.name.trim(),
    contact: !!(form.contact || "").trim(),
    phone: !!form.phone && isValidPhone(form.phone),
    email: !!(form.email || "").trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.email || "").trim()),
    tax_no: !!(form.tax_no || "").trim(),
    tax_office: !feature("field.tax_office") || form.type !== "corporate" || !!(form.tax_office || "").trim(),
    province: !!form.province,
    district: !!form.district,
    address: !!(form.address || "").trim(),
    verified: !!(form.tax_verified_at || "").trim(),
  };
  const missLabels = {
    name: form.type === "corporate" ? "Firma Tam Ünvanı" : "Ad Soyad", contact: "Yetkili Kişi",
    phone: "Telefon", email: "E-posta", tax_no: form.type === "corporate" ? "VKN" : "TCKN",
    tax_office: "Vergi Dairesi", province: "İl", district: "İlçe", address: "Açık Adres",
    verified: "VKN/TCKN Doğrulaması (Doğrula & Getir)",
  };
  const missing = Object.keys(req).filter((k) => !req[k]).map((k) => missLabels[k]);

  return (
    <div className="fade-in">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading font-bold text-slate-800">Tedarikçiler ({list.length})</p>
        <div className="relative ml-auto sm:ml-4">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tedarikçi ara (ad/telefon/VKN/kod)..." className="w-56 pl-9" />
        </div>
        <div className="flex gap-2">
          <input id="supplier-import-file" type="file" accept=".xlsx" className="hidden" data-testid="supplier-import-input" onChange={importExcel} />
          {canEdit && (
            <Button data-testid="supplier-import-btn" onClick={() => document.getElementById("supplier-import-file").click()} variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/5">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel'den Al
            </Button>
          )}
          <ColumnToggle testid="suppliers-table-tools" cols={SUPP_COLS} visible={colVis} onToggle={toggleCol} density={rowDensity} onDensity={setColDensity} onReset={resetCols} />
          <Button data-testid="supplier-export-btn" onClick={exportExcel} variant="outline" className="border-slate-300">
            <FileSpreadsheet className="mr-1.5 h-4 w-4 text-emerald-600" /> Excel'e At
          </Button>
          {canEdit && (
            <Button data-testid="supplier-new-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Tedarikçi
            </Button>
          )}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200" data-testid="supplier-filters">
        <span className="text-xs font-semibold text-slate-500">Filtrele:</span>
        <select data-testid="supplier-filter-category" value={fCategory} onChange={(e) => setFCategory(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
          <option value="">Tüm Tedarik Kategorileri</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select data-testid="supplier-filter-province" value={fProvince} onChange={(e) => { setFProvince(e.target.value); if (e.target.value) setFRegion(""); }}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
          <option value="">Tüm İller</option>
          {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="supplier-filter-region" value={fRegion} disabled={!!fProvince} onChange={(e) => setFRegion(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-50">
          <option value="">Tüm Bölgeler</option>
          {TR_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {(fCategory || fProvince || fRegion) && (
          <button data-testid="supplier-filter-clear" onClick={() => { setFCategory(""); setFProvince(""); setFRegion(""); }}
            className="text-xs font-semibold text-red-500 hover:underline">Temizle</button>
        )}
        <span className="ml-auto text-xs text-slate-400">{list.length} tedarikçi</span>
      </div>

      {canEdit && selected.size > 0 && (
        <div data-testid="suppliers-bulk-bar" className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-brand-green/5 p-2 ring-1 ring-inset ring-brand-green/30">
          <span className="text-sm font-semibold text-brand-green">{selected.size} tedarikçi seçili</span>
          <Button data-testid="suppliers-bulk-tag" onClick={bulkTag} size="sm" variant="outline" className="border-brand-green/40 text-brand-green hover:bg-brand-green/10"><Tag className="mr-1.5 h-4 w-4" /> Toplu Etiket</Button>
          <Button data-testid="suppliers-bulk-csv" onClick={exportSelectedCsv} size="sm" variant="outline" className="border-slate-300"><Download className="mr-1.5 h-4 w-4" /> Seçilenleri CSV</Button>
          <button data-testid="suppliers-bulk-clear" onClick={() => setSelected(new Set())} className="ml-auto text-xs font-semibold text-slate-500 hover:underline">Seçimi temizle</button>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${rowDensity === "compact" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {filtered.length === 0 && <p className="text-sm text-slate-400">{q ? "Eşleşen tedarikçi yok." : "Kayıtlı tedarikçi yok."}</p>}
        {suppPaged.slice.map((s) => (
          <div key={s.id} data-testid={`supplier-card-${s.id}`} className={`rounded-md bg-white ${rowDensity === "compact" ? "p-3" : "p-4"} ring-1 ring-inset ring-slate-200`}>
            <div className="mb-2 flex items-start justify-between">
              <div className="flex items-center gap-2">
                {canEdit && <input type="checkbox" data-testid={`supplier-select-${s.id}`} checked={selected.has(s.id)} onChange={() => toggleSel(s.id)} className="h-4 w-4 accent-brand-green" />}
                <EntityAvatar src={s.avatar_url} name={s.name} size={40} />
                <div>
                  <p className="font-heading font-bold text-slate-800">{s.name}</p>
                  <p className="flex items-center gap-1 text-[11px] text-slate-400"><Hash className="h-3 w-3" />{s.code || "—"} · {s.type === "individual" ? "Bireysel" : "Kurumsal"}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`supplier-summary-${s.id}`} onClick={() => setSummaryId(s.id)} title="Performans & Bakiye" className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><TrendingUp className="h-4 w-4" /></button>
                <button data-testid={`supplier-detail-${s.id}`} onClick={() => openDetail(s)} title="Detay & Evraklar" className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><Eye className="h-4 w-4" /></button>
                {canEdit && <button data-testid={`supplier-edit-${s.id}`} onClick={() => openEdit(s)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>}
                {canEdit && <button data-testid={`supplier-del-${s.id}`} onClick={() => del(s)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-500">
              {colVis.contact && s.contact && <p className="flex items-center gap-1.5"><User className="h-3 w-3" /> {s.contact}</p>}
              {colVis.contact && s.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {s.phone}</p>}
              {colVis.contact && s.email && <p className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {s.email}</p>}
              {colVis.location && (s.province || s.district) && <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {[s.district, s.province].filter(Boolean).join(" / ")}</p>}
              {colVis.tax && s.tax_no && <p>V/TC No: <span className="tabular">{s.tax_no}</span></p>}
              {colVis.terms && s.payment_term_days != null && <p>Ödeme Vadesi: <b className="text-slate-600">{s.payment_term_days === 0 ? "Peşin" : `${s.payment_term_days} gün`}</b></p>}
            </div>
            {(s.tags || []).length > 0 && (
              <div data-testid={`supplier-tags-${s.id}`} className="mt-2 flex flex-wrap gap-1">
                {s.tags.map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"><Tag className="h-3 w-3" /> {t}</span>)}
              </div>
            )}
            {colVis.cats && (s.supply_categories || []).length > 0 && (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <p className="overline mb-1 flex items-center gap-1"><Layers className="h-3 w-3 text-emerald-600" /> Tedarik Kategorileri</p>
                <div className="flex flex-wrap gap-1">
                  {s.supply_categories.map((c, i) => (
                    <span key={i} className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">{catLabel(c)}</span>
                  ))}
                </div>
                {s.product_note && <p className="mt-1.5 text-[11px] italic text-slate-500">Not: {s.product_note}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {filtered.length > 0 && (
        <div className="mt-3">
          <Pagination page={suppPaged.page} pageSize={suppPaged.pageSize} total={suppPaged.total}
            onPage={suppPaged.setPage} onPageSize={suppPaged.setPageSize} sizes={[12, 24, 48, 96]} testid="supplier-pagination" />
        </div>
      )}

      <SupplierSummary supplierId={summaryId} onClose={() => setSummaryId(null)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Tedarikçiyi Düzenle" : "Yeni Tedarikçi"}</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-red-500">*</span> işaretli alanların tümü ve <b>VKN/TCKN doğrulaması</b> zorunludur — eksikse kayıt yapılamaz.{editing?.code ? ` · Kod: ${editing.code}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pb-1"><AvatarUpload value={form.avatar_url} name={form.name} onChange={(url) => setForm({ ...form, avatar_url: url })} size={64} /></div>
          <Tabs defaultValue="identity" className="w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-lg bg-slate-100 p-1" data-testid="supplier-tabs">
              <TabsTrigger value="identity" data-testid="sup-tab-identity" className="flex-1">Kimlik</TabsTrigger>
              <TabsTrigger value="address" data-testid="sup-tab-address" className="flex-1">Adres & Vergi</TabsTrigger>
              <TabsTrigger value="commercial" data-testid="sup-tab-commercial" className="flex-1">Ticari & Kategori</TabsTrigger>
            </TabsList>
            <div className="mt-3">
              <TabsContent value="identity" className="mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{form.type === "corporate" ? "Firma Tam Ünvanı *" : "Ad Soyad *"}</Label><Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Örn. Hal Tedarik A.Ş." /></div>
                  <div>
                    <Label>Tür</Label>
                    <div className="mt-1 inline-flex rounded-md bg-slate-100 p-1">
                      {[["corporate", "Kurumsal"], ["individual", "Bireysel"]].map(([id, label]) => (
                        <button key={id} type="button" data-testid={`supplier-type-${id}`} onClick={() => setForm({ ...form, type: id })}
                          className={`rounded-sm px-3 py-1 text-xs font-semibold ${form.type === id ? "bg-brand-green text-white" : "text-slate-500"}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div><Label>Yetkili Kişi *</Label><Input data-testid="supplier-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1" placeholder="Ad Soyad" /></div>
                  <div><Label>Telefon *</Label><div className="mt-1"><PhoneInput testid="supplier-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></div></div>
                  <div><Label>E-posta *</Label><Input data-testid="supplier-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                </div>
              </TabsContent>
              <TabsContent value="address" className="mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{form.type === "corporate" ? "VKN *" : "TCKN *"}</Label><Input data-testid="supplier-taxno" value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} className="mt-1 tabular" /></div>
                  {form.type === "corporate" && feature("field.tax_office") && (
                    <div><Label>Vergi Dairesi *</Label><TaxOfficePicker testid="supplier-taxoffice" value={form.tax_office} onChange={(v) => setForm({ ...form, tax_office: v })} /></div>
                  )}
                  <div>
                    <Label>İl *</Label>
                    <select data-testid="supplier-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value, district: "" })}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                      <option value="">İl seçin</option>
                      {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>İlçe *</Label>
                    <select data-testid="supplier-district" value={form.district} disabled={!form.province} onChange={(e) => setForm({ ...form, district: e.target.value })}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">İlçe seçin</option>
                      {(TR_CITIES[form.province] || []).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><Label>Açık Adres *</Label><Input data-testid="supplier-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="mt-3">
                  <TaxPortalLookup taxNo={form.tax_no} type={form.type} portal={form.e_invoice_portal} docType={form.e_document_type}
                    verifiedAt={form.tax_verified_at} name={form.name}
                    onSelect={({ e_document_type, e_invoice_portal }) => setForm((f) => ({ ...f, e_document_type, e_invoice_portal }))}
                    onEmail={(email) => setForm((f) => ({ ...f, email }))}
                    onVerified={(ts) => setForm((f) => ({ ...f, tax_verified_at: ts }))} testid="supplier-tax-portal" />
                </div>
              </TabsContent>
              <TabsContent value="commercial" className="mt-0 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Ödeme Vadesi (gün)</Label><Input data-testid="supplier-payment-term" type="number" min="0" value={form.payment_term_days} onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} className="mt-1" placeholder="0 = peşin · 30 · 45..." /></div>
                  <div><Label>Kredi Limiti ({curSym()})</Label><PriceInput data-testid="supplier-credit-limit" type="number" min="0" step="0.01" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} wrapperClassName="mt-1" className="w-full" placeholder="Opsiyonel" /></div>
                  <div className="col-span-2"><Label>Not</Label><Input data-testid="supplier-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" placeholder="Min. sipariş, teslim koşulu vb." /></div>
                </div>

                <div className="rounded-md bg-emerald-50/50 p-3 ring-1 ring-inset ring-emerald-100">
                  <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-emerald-800"><Layers className="h-4 w-4 text-emerald-600" /> Tedarik Kategorileri</p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[160px] flex-1">
                      <Label className="text-[11px] text-slate-500">Kategori</Label>
                      <select data-testid="supplier-category" value={selCat} onChange={(e) => { setSelCat(e.target.value); setSelSub(""); }}
                        className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                        <option value="">Kategori seçin</option>
                        {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="min-w-[160px] flex-1">
                      <Label className="text-[11px] text-slate-500">Alt Kategori (opsiyonel)</Label>
                      <select data-testid="supplier-subcategory" value={selSub} disabled={!selCat} onChange={(e) => setSelSub(e.target.value)}
                        className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-50">
                        <option value="">Tümü</option>
                        {subsOf(selCat).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <Button type="button" data-testid="supplier-add-cat" onClick={addCat} disabled={!selCat} className="h-10 bg-brand-green text-white hover:bg-brand-green-600">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {form.supply_categories.length === 0 && <p className="text-[11px] text-emerald-700/70">Henüz kategori eklenmedi.</p>}
                    {form.supply_categories.map((c, i) => (
                      <span key={i} data-testid={`supplier-cat-tag-${i}`} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                        {catLabel(c)}
                        <button type="button" onClick={() => rmCat(i)} className="ml-0.5 text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="mt-2">
                    <Label className="text-[11px] text-slate-500">Spesifik Ürün Notu (opsiyonel)</Label>
                    <Input data-testid="supplier-product-note" value={form.product_note} onChange={(e) => setForm({ ...form, product_note: e.target.value })}
                      className="mt-1" placeholder="Çok spesifik bir ürünü varsa buraya yazın (ör. İthal Avokado Hass)" />
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          {missing.length > 0 && (
            <p data-testid="supplier-required-hint" className={`mt-3 rounded-md px-3 py-2 text-[11px] font-medium ring-1 ring-inset ${isAdmin ? "bg-sky-50 text-sky-700 ring-sky-200" : "bg-amber-50 text-amber-700 ring-amber-200"}`}>
              {isAdmin ? "ℹ Yönetici: eksik bilgiyle de kaydedebilirsiniz — eksik: " : "⚠ Kayıt için zorunlu (eksik): "}{missing.join(", ")}
            </p>
          )}
          <Button data-testid="supplier-save" onClick={requestSave} disabled={saving || (!isAdmin && missing.length > 0)} className="mt-3 w-full bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Kaydet" : "Ekle"}
          </Button>
        </DialogContent>
      </Dialog>
      {detail && (
        <EntityDetailDialog open onClose={() => setDetail(null)} testid="supplier-detail-dialog"
          title={detail.name} avatarSrc={detail.avatar_url} avatarName={detail.name}
          subtitle={`${detail.type === "individual" ? "Bireysel" : "Kurumsal"}${detail.code ? ` · ${detail.code}` : ""}`}
          sections={[
            { heading: "İletişim", rows: [
              { label: "Yetkili", value: detail.contact },
              { label: "Telefon", value: detail.phone },
              { label: "E-posta", value: detail.email },
            ]},
            { heading: "Adres & Vergi", rows: [
              { label: "Vergi Dairesi", value: detail.tax_office },
              { label: "VKN / TCKN", value: detail.tax_no },
              { label: "İl / İlçe", value: [detail.district, detail.province].filter(Boolean).join(" / ") },
              { label: "Açık Adres", value: detail.address, full: true },
            ]},
            { heading: "Ticari", rows: [
              { label: "Ödeme Vadesi", value: detail.payment_term_days != null ? (detail.payment_term_days === 0 ? "Peşin" : `${detail.payment_term_days} gün`) : "—" },
              { label: "Kredi Limiti", value: detail.credit_limit != null ? `${detail.credit_limit} ${curSym()}` : "—" },
              { label: "Not", value: detail.note, full: true },
              { label: "Ürün Notu", value: detail.product_note, full: true },
            ], extra: (detail.supply_categories || []).length > 0 ? (
              <div className="mt-2">
                <p className="mb-1 text-[11px] font-semibold text-slate-500">Tedarik Kategorileri</p>
                <div className="flex flex-wrap gap-1">
                  {detail.supply_categories.map((c, i) => <span key={i} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">{catLabel(c)}</span>)}
                </div>
              </div>
            ) : null },
          ]}
          docs={detailDocs}
          docConfig={{ canWrite: canEdit, onAdd: addSupplierDoc, onDelete: delSupplierDoc, testid: "supplier-docs", zipPath: `/suppliers/${detail.id}/documents/zip` }}
          footer={<button data-testid="supplier-detail-summary" onClick={() => { const id = detail.id; setDetail(null); setSummaryId(id); }} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-green px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-green-600"><TrendingUp className="h-4 w-4" /> Cari Ekstre / Performans</button>} />
      )}
      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} loading={saving} onConfirm={doSave}
        title={editing ? "Tedarikçiyi güncelle" : "Tedarikçi ekle"}
        description={`"${form.name}" tedarikçisini kaydetmek istediğinize emin misiniz?`}
        confirmText={editing ? "Evet, kaydet" : "Evet, ekle"} />
    </div>
  );
}
